import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Typography,
  Box,
  Divider,
  AppBar,
  Toolbar,
  useMediaQuery,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useTheme } from "@mui/material/styles";

export default function AppDialog({
  open,
  onClose,
  title,
  subtitle,
  description,
  icon = null,
  children,
  actions,
  maxWidth = "sm",
  fullWidth = true,
  fullScreenMobile = false,
  fullScreenBreakpoint = "sm",
  dividers = false,
  showCloseButton = true,
  stickyHeader = false,
  paperSx = {},
  contentSx = {},
  actionsSx = {},
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down(fullScreenBreakpoint));
  const fullScreen = fullScreenMobile ? isMobile : false;
  const isDark = theme.palette.mode === "dark";

  // Vidrio estilo Apple: translúcido con blur moderado y saturación, para que
  // lo de atrás se siga reconociendo. Un blur alto (≈28px) promedia la página
  // blanca/gris en una mancha uniforme y el modal se ve gris; el velo de atrás
  // tampoco se desenfoca ni se oscurece casi, por la misma razón.
  const glassBlur = isDark
    ? "blur(10px) saturate(170%)"
    : "blur(10px) saturate(170%) brightness(1.06)";

  const defaultPaperSx = {
    borderRadius: fullScreen ? 0 : 5,
    overflow: "hidden",
    background: isDark
      ? "linear-gradient(160deg, rgba(51,65,85,0.55) 0%, rgba(15,23,42,0.50) 100%)"
      : "linear-gradient(160deg, rgba(255,255,255,0.58) 0%, rgba(255,255,255,0.34) 100%)",
    backdropFilter: glassBlur,
    WebkitBackdropFilter: glassBlur,
    border: isDark
      ? "1px solid rgba(255,255,255,0.14)"
      : "1px solid rgba(255,255,255,0.70)",
    boxShadow: fullScreen
      ? "none"
      : isDark
        ? "0 24px 70px rgba(0,0,0,0.45), inset 0 1px 0 rgba(255,255,255,0.10)"
        : "0 24px 70px rgba(15,23,42,0.16), 0 4px 14px rgba(15,23,42,0.06), inset 0 1px 0 rgba(255,255,255,0.85)",
  };

  const closeButtonSx = {
    borderRadius: 2.5,
    bgcolor: isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)",
    border: isDark
      ? "1px solid rgba(255,255,255,0.08)"
      : "1px solid rgba(0,0,0,0.06)",
    transition: "all .18s ease",
    flexShrink: 0,
    "&:hover": {
      bgcolor: isDark ? "rgba(255,255,255,0.10)" : "rgba(0,0,0,0.08)",
      transform: "translateY(-1px)",
    },
  };

  const borderCol = isDark ? "rgba(255,255,255,0.10)" : "rgba(15,23,42,0.07)";

  const HeaderContent = (
    <Box
      sx={{
        width: "100%",
        display: "flex",
        alignItems: { xs: "flex-start", sm: "center" },
        justifyContent: "space-between",
        gap: 1.5,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.4,
          minWidth: 0,
          flex: 1,
        }}
      >
        {icon && (
          <Box
            sx={{
              width: { xs: 46, sm: 56 },
              height: { xs: 46, sm: 56 },
              borderRadius: 3,
              display: "grid",
              placeItems: "center",
              flexShrink: 0,
              background: isDark
                ? "rgba(25,118,210,0.12)"
                : "rgba(255,255,255,0.72)",
              backdropFilter: "blur(10px)",
              WebkitBackdropFilter: "blur(10px)",
              border: "1px solid rgba(47, 147, 247, 0.22)",
              boxShadow: isDark ? "none" : "0 8px 22px rgba(0,0,0,0.08)",
              color: "primary.main",
              "& svg": {
                fontSize: { xs: 24, sm: 28 },
                display: "block",
              },
            }}
          >
            {icon}
          </Box>
        )}

        <Box sx={{ minWidth: 0 }}>
          {title && (
            <Typography
              sx={{
                fontWeight: 950,
                fontSize: { xs: 18, sm: 20 },
                lineHeight: 1.15,
                letterSpacing: 0.1,
              }}
            >
              {title}
            </Typography>
          )}

          {(subtitle || description) && (
            <Typography
              sx={{
                fontSize: 13.5,
                opacity: 0.72,
                mt: 0.4,
                lineHeight: 1.35,
              }}
            >
              {subtitle || description}
            </Typography>
          )}
        </Box>
      </Box>

      {showCloseButton && (
        <IconButton onClick={onClose} sx={closeButtonSx}>
          <CloseIcon />
        </IconButton>
      )}
    </Box>
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth={fullWidth}
      maxWidth={maxWidth}
      fullScreen={fullScreen}
      PaperProps={{
        sx: {
          ...defaultPaperSx,
          ...paperSx,
        },
      }}
      BackdropProps={{
        sx: {
          background: isDark ? "rgba(2,6,23,0.30)" : "rgba(15,23,42,0.05)",
        },
      }}
    >
      {stickyHeader ? (
        <AppBar
          position="sticky"
          elevation={0}
          sx={{
            // Sin fondo ni blur propios: un segundo backdrop-filter dentro del
            // vidrio solo ve el blanco del modal y el encabezado queda opaco.
            // El contenido scrollea en DialogContent, nunca pasa por debajo.
            background: "transparent",
            backgroundImage: "none",
            borderBottom: `1px solid ${borderCol}`,
            color: "text.primary",
            boxShadow: "none",
          }}
        >
          <Toolbar
            sx={{
              px: { xs: 1.5, sm: 2.2 },
              py: 1.2,
              minHeight: { xs: 72, sm: 82 },
              alignItems: "center",
            }}
          >
            {HeaderContent}
          </Toolbar>
        </AppBar>
      ) : (
        <>
          <DialogTitle
            sx={{
              px: { xs: 1.5, sm: 2.2 },
              pt: { xs: 1.6, sm: 2 },
              pb: 1.4,
              background: "transparent",
            }}
          >
            {HeaderContent}
          </DialogTitle>
          <Divider sx={{ borderColor: borderCol }} />
        </>
      )}

      <DialogContent
        dividers={dividers}
        sx={{
          px: { xs: 1.5, sm: 2.2 },
          py: { xs: 1.5, sm: 2 },
          background: "transparent",
          borderColor: borderCol,
          ...contentSx,
        }}
      >
        {children}
      </DialogContent>

      {actions && (
        <DialogActions
          sx={{
            px: { xs: 1.5, sm: 2.2 },
            gap: 1,
            borderTop: `1px solid ${borderCol}`,
            background: "transparent",
            ...actionsSx,
          }}
        >
          {actions}
        </DialogActions>
      )}
    </Dialog>
  );
}
